import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

const SybilDefenderDashboard = () => {
  const [data, setData] = useState({
    uniswapActivity: [],
    manipulatedContracts: [],
    washTradedTokens: [],
    recentClusters: [],
    falsePositives: []
  });
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    // Fetch dashboard data
    // For now, using mock data - replace with actual API calls
    setTimeout(() => {
      setData({
        uniswapActivity: [
          { wallet: '0x1234...5678', trades: 145, suspicionScore: 0.92 },
          { wallet: '0xabcd...ef12', trades: 98, suspicionScore: 0.87 },
          { wallet: '0x9876...5432', trades: 76, suspicionScore: 0.81 }
        ],
        manipulatedContracts: [
          { contract: '0xdao1...', name: 'GovernanceDAO', anomalies: 23 },
          { contract: '0xdao2...', name: 'VotingProtocol', anomalies: 18 }
        ],
        washTradedTokens: [
          { token: 'SHIB', volume: '$2.3M', sybilPercentage: 67 },
          { token: 'PEPE', volume: '$1.8M', sybilPercentage: 54 }
        ],
        recentClusters: [
          { clusterId: 'CLU-001', size: 234, detected: '2 hours ago' },
          { clusterId: 'CLU-002', size: 156, detected: '5 hours ago' },
          { clusterId: 'CLU-003', size: 89, detected: '8 hours ago' }
        ],
        falsePositives: [
          { wallet: '0xfpwallet...', reviewed: 'Verified legitimate', date: 'Nov 10' }
        ]
      });
      setLoading(false);
    }, 1000);
  }, []);

  return (
    <div className="bg-dark text-light" style={{ minHeight: '100vh' }}>
      {/* Header */}
      <nav className="navbar navbar-dark bg-black border-bottom border-secondary">
        <div className="container-fluid">
          <a className="navbar-brand" href="/">
            <i className="bi bi-shield-exclamation me-2"></i>
            Sybil Defender
          </a>
          <div className="d-flex gap-2">
            <button
              className="btn btn-outline-primary btn-sm"
              onClick={() => navigate('/register')}
            >
              Get API Access
            </button>
            <a href="/" className="btn btn-outline-light btn-sm">Back to Home</a>
          </div>
        </div>
      </nav>

      <div className="container my-5">
        {/* Hero */}
        <div className="text-center mb-5">
          <h1 className="display-5 mb-3">
            <i className="bi bi-activity text-danger me-2"></i>
            Live Sybil Activity on Ethereum Mainnet
          </h1>
          <p className="lead text-secondary">
            Data updated every 24 hours
          </p>
        </div>

        {loading ? (
          <div className="text-center py-5">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
            <p className="mt-3 text-secondary">Loading dashboard data...</p>
          </div>
        ) : (
          <>
            {/* Sybil Activity on Uniswap */}
            <div className="card bg-secondary-subtle text-light mb-4">
              <div className="card-body">
                <h4 className="card-title mb-3">
                  <i className="bi bi-arrow-left-right text-warning me-2"></i>
                  Sybil Activity on Uniswap
                </h4>
                <p className="text-secondary small mb-3">Top detected Sybil trades</p>
                <div className="table-responsive">
                  <table className="table table-dark table-hover">
                    <thead>
                      <tr>
                        <th>Wallet</th>
                        <th>Trades</th>
                        <th>Suspicion Score</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.uniswapActivity.map((item, idx) => (
                        <tr key={idx}>
                          <td><code>{item.wallet}</code></td>
                          <td>{item.trades}</td>
                          <td>
                            <span className={`badge ${item.suspicionScore > 0.85 ? 'bg-danger' : 'bg-warning'}`}>
                              {(item.suspicionScore * 100).toFixed(0)}%
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>

            {/* Most Manipulated Governance Contracts */}
            <div className="card bg-secondary-subtle text-light mb-4">
              <div className="card-body">
                <h4 className="card-title mb-3">
                  <i className="bi bi-diagram-3 text-info me-2"></i>
                  Most Manipulated Governance Contracts
                </h4>
                <p className="text-secondary small mb-3">DAO voting anomalies</p>
                <div className="table-responsive">
                  <table className="table table-dark table-hover">
                    <thead>
                      <tr>
                        <th>Contract</th>
                        <th>Name</th>
                        <th>Anomalies Detected</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.manipulatedContracts.map((item, idx) => (
                        <tr key={idx}>
                          <td><code>{item.contract}</code></td>
                          <td>{item.name}</td>
                          <td><span className="badge bg-warning">{item.anomalies}</span></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>

            {/* Highest Wash-Traded Tokens */}
            <div className="card bg-secondary-subtle text-light mb-4">
              <div className="card-body">
                <h4 className="card-title mb-3">
                  <i className="bi bi-currency-exchange text-success me-2"></i>
                  Highest Wash-Traded Tokens
                </h4>
                <p className="text-secondary small mb-3">By Sybil clusters</p>
                <div className="table-responsive">
                  <table className="table table-dark table-hover">
                    <thead>
                      <tr>
                        <th>Token</th>
                        <th>Volume</th>
                        <th>Sybil %</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.washTradedTokens.map((item, idx) => (
                        <tr key={idx}>
                          <td><strong>{item.token}</strong></td>
                          <td>{item.volume}</td>
                          <td>
                            <span className="badge bg-danger">{item.sybilPercentage}%</span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>

            {/* Recently Detected Sybil Clusters */}
            <div className="card bg-secondary-subtle text-light mb-4">
              <div className="card-body">
                <h4 className="card-title mb-3">
                  <i className="bi bi-people-fill text-primary me-2"></i>
                  Recently Detected Sybil Clusters
                </h4>
                <p className="text-secondary small mb-3">Latest addresses</p>
                <div className="table-responsive">
                  <table className="table table-dark table-hover">
                    <thead>
                      <tr>
                        <th>Cluster ID</th>
                        <th>Size (Wallets)</th>
                        <th>Detected</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.recentClusters.map((item, idx) => (
                        <tr key={idx}>
                          <td><code>{item.clusterId}</code></td>
                          <td>{item.size}</td>
                          <td className="text-secondary">{item.detected}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>

            {/* False Positives Reviewed */}
            <div className="card bg-secondary-subtle text-light mb-4">
              <div className="card-body">
                <h4 className="card-title mb-3">
                  <i className="bi bi-check-circle text-success me-2"></i>
                  False Positives Reviewed
                </h4>
                <p className="text-secondary small mb-3">Transparency and trust</p>
                <div className="table-responsive">
                  <table className="table table-dark table-hover">
                    <thead>
                      <tr>
                        <th>Wallet</th>
                        <th>Status</th>
                        <th>Date</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.falsePositives.map((item, idx) => (
                        <tr key={idx}>
                          <td><code>{item.wallet}</code></td>
                          <td><span className="badge bg-success">{item.reviewed}</span></td>
                          <td className="text-secondary">{item.date}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>

            {/* CTA */}
            <div className="card bg-primary text-light text-center mt-5">
              <div className="card-body py-5">
                <h3 className="mb-3">Want full access to raw Sybil data and API queries?</h3>
                <p className="mb-4">Get programmatic access to detect Sybil activity in real-time</p>
                <button
                  className="btn btn-light btn-lg px-5"
                  onClick={() => navigate('/register')}
                >
                  Get API Access
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default SybilDefenderDashboard;


